import { createClient, createCookieAuth } from "@calorio/api-client";
import { deviceTimeZone, useProfile, type CalorioContext } from "@calorio/core";
import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Wires the API client and the user's timezone into one context.
 *
 * Both paths are same-origin on purpose — see `vite.config.ts`. The browser
 * holds no token; it attaches Authgate's HttpOnly cookie, and the API accepts
 * it because the request comes from the app's own origin.
 */
const ApiContext = createContext<CalorioContext | null>(null);

export function CalorioProvider({ children }: { children: ReactNode }) {
  const value = useMemo<CalorioContext>(() => {
    const auth = createCookieAuth("");
    return {
      api: createClient("/api", auth),
      // Provisional: the device's zone, replaced by the profile's below once
      // it loads. Days are bucketed server-side by the *profile* zone, so a
      // freshly-landed traveller must not silently ask for a different day.
      timeZone: deviceTimeZone(),
    };
  }, []);

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/**
 * The bare API client, without the profile lookup `useCalorio` performs.
 *
 * The session probe needs this: `useCalorio` fires `useProfile`, and asking for
 * a profile is exactly what cannot be done before we know whether anyone is
 * signed in.
 */
export function useApi(): CalorioContext["api"] {
  const base = useContext(ApiContext);
  if (!base) throw new Error("useApi must be used inside <CalorioProvider>");
  return base.api;
}

/**
 * The context for a screen that has to render for somebody with no account.
 *
 * The same base as `useCalorio`, minus the profile lookup — for the reason
 * `useApi` above gives, applied to a whole screen rather than to the session
 * probe: a shared meal opens for a stranger, and asking the API who they are is
 * a request that can only be answered 401. The device's zone stands in, and
 * nothing on that page reads it: the meal is the sender's, and what the visitor
 * logs is filed under the day the server computes from their own profile.
 */
export function useAnonymousCalorio(): CalorioContext {
  const base = useContext(ApiContext);
  if (!base) throw new Error("useAnonymousCalorio must be used inside <CalorioProvider>");
  return base;
}

/** The API client plus the timezone the server buckets days by. */
export function useCalorio(): CalorioContext {
  const base = useContext(ApiContext);
  if (!base) throw new Error("useCalorio must be used inside <CalorioProvider>");

  const profile = useProfile(base);

  return useMemo(
    () => ({ ...base, timeZone: profile.data?.timezone ?? base.timeZone }),
    [base, profile.data?.timezone],
  );
}
